<template>
  <ol data-slot="lead-activity-timeline" class="m-0 flex list-none flex-col p-0">
    <li v-for="entry in entries" :key="entry.key" class="relative flex gap-3 pb-5 last:pb-0">
      <span class="absolute top-4 bottom-0 left-[5px] w-px bg-border" aria-hidden="true" />
      <span :class="[ 'relative mt-1.5 size-[11px] shrink-0 rounded-full', entry.dot ]" aria-hidden="true" />
      <div class="flex min-w-0 flex-1 flex-col gap-1">
        <p class="m-0 leading-relaxed text-foreground">
          <span v-for="(part, index) in entry.parts" :key="index" :class="partClass(part)">{{ part.status ? tr(part.status.label) : part.text }}</span>
        </p>
        <span class="text-xs text-muted-foreground tabular-nums">{{ formatDateTime(entry.createdAt) }}</span>
        <p v-if="entry.note" class="m-0 mt-1 rounded-lg bg-foreground/5 px-3 py-2 whitespace-pre-line text-muted-foreground [overflow-wrap:anywhere]">{{ entry.note }}</p>
      </div>
    </li>
  </ol>
</template>

<script>
import LeadgenFormLeadActivity from '@/entities/LeadgenFormLeadActivity';
import { leadStatusStyle } from '@/ui/utils/leadStatus.js';
import { formatTime, formatToYMD } from '@/utils/date.js';

export default {
  name: 'LeadActivityTimeline',
  props: {
    // Newest first, as the backend sends them.
    activities: {
      type: Array,
      default: () => [],
    },
    // The lead's arrival closes the history.
    createdAt: {
      type: Date,
      default: null,
    },
  },
  computed: {
    entries() {
      const entries = this.activities.map(activity => ({
        key: 'activity--' + activity.id,
        createdAt: activity.created_at,
        dot: this.dotClass(activity),
        parts: this.describe(activity),
        note: activity.type === LeadgenFormLeadActivity.TYPE_COMMENT ? activity.details?.text : null,
      }));

      if (this.createdAt) {
        entries.push({
          key: 'created',
          createdAt: this.createdAt,
          dot: 'bg-muted-foreground',
          parts: [ { text: this.tr('The lead arrived from the form') } ],
          note: null,
        });
      }

      return entries;
    },
  },
  methods: {
    formatDateTime(date) {
      return date ? formatToYMD(date) + ' ' + formatTime(date) : '';
    },
    partClass(part) {
      if (part.status) {
        return [ 'mx-0.5 inline-flex rounded-md px-1.5 py-0.5 text-xs font-medium', part.status.chip ];
      }

      return part.strong ? 'font-semibold' : null;
    },
    userName(user) {
      return user ? (user.firstname + ' ' + user.lastname).trim() : this.tr('Unknown user');
    },
    dotClass(activity) {
      if (activity.type === LeadgenFormLeadActivity.TYPE_CONVERSION_FAILED) {
        return 'bg-destructive';
      }
      if (activity.type === LeadgenFormLeadActivity.TYPE_CONVERSION_SENT) {
        return 'bg-success';
      }

      return 'bg-primary';
    },
    // The translated sentence cut at its placeholders, which become names and status chips.
    toParts(text, values) {
      const placeholders = Object.keys(values);
      if (placeholders.length === 0) {
        return [ { text } ];
      }

      const pattern = new RegExp('(' + placeholders.map(key => key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|') + ')');

      return text.split(pattern)
        .filter(piece => piece !== '')
        .map(piece => (piece in values ? values[piece] : { text: piece }));
    },
    sentence(key, values = {}) {
      // tr() leaves the placeholders in place when no replacements are given.
      return this.toParts(this.tr(key), values);
    },
    describe(activity) {
      const details = activity.details ?? {};
      const user = { text: this.userName(activity.user), strong: true };

      switch (activity.type) {
        case LeadgenFormLeadActivity.TYPE_STATUS_CHANGED:
          return this.sentence(':user changed the status from :from to :to', {
            ':user': user,
            ':from': { status: leadStatusStyle(details.from) },
            ':to': { status: leadStatusStyle(details.to) },
          });

        case LeadgenFormLeadActivity.TYPE_ASSIGNED:
          if (!details.to) {
            return this.sentence(':user removed the assignee', { ':user': user });
          }
          if (details.to.id && details.to.id === activity.user?.id) {
            return this.sentence(':user claimed this lead', { ':user': user });
          }

          return this.sentence(':user assigned the lead to :assignee', {
            ':user': user,
            ':assignee': { text: this.userName(details.to), strong: true },
          });

        case LeadgenFormLeadActivity.TYPE_COMMENT:
          return this.sentence(':user added a note', { ':user': user });

        case LeadgenFormLeadActivity.TYPE_CONVERSION_SENT:
          return this.sentence('The :status status was sent to Meta', {
            ':status': { status: leadStatusStyle(details.status) },
          });

        case LeadgenFormLeadActivity.TYPE_CONVERSION_FAILED:
          if (details.reason === 'no_pixel') {
            return this.sentence('The :status status was not sent to Meta: no pixel is set for the form or its automation', {
              ':status': { status: leadStatusStyle(details.status) },
            });
          }

          return this.sentence('Sending the :status status to Meta failed: :message', {
            ':status': { status: leadStatusStyle(details.status) },
            ':message': { text: details.message ?? '' },
          });

        default:
          return [];
      }
    },
  },
};
</script>
